import React, { useContext, useEffect, useState } from "react";
import { CommonContext } from "hooks/CommonContext";
import JoinHeader from "./join-pages/JoinHeader";
import FeedbackPopup from "./FeedbackPopup";
import { FaCheckCircle, FaComments } from "react-icons/fa";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  .ei-page {
    min-height: calc(100vh - 56px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    background: #080d18;
    background-image:
      radial-gradient(ellipse 55% 50% at 15% 60%, rgba(14,165,233,0.05) 0%, transparent 65%),
      radial-gradient(ellipse 45% 45% at 85% 35%, rgba(99,102,241,0.06) 0%, transparent 65%);
    font-family: 'Inter', system-ui, sans-serif;
    box-sizing: border-box;
  }

  .ei-card {
    width: 100%;
    max-width: 640px;
    background: #0f1629;
    border: 1px solid #1e2d4a;
    border-radius: 20px;
    padding: 52px 44px;
    text-align: center;
    box-shadow: 0 24px 64px rgba(0,0,0,0.5);
  }

  /* Success icon */
  .ei-icon {
    width: 96px;
    height: 96px;
    margin: 0 auto 28px;
    border-radius: 50%;
    background: rgba(34, 197, 94, 0.10);
    border: 1.5px solid rgba(34, 197, 94, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #4ade80;
    font-size: 42px;
  }

  .ei-title {
    font-size: clamp(22px, 3vw, 28px);
    font-weight: 700;
    color: #f0f4ff;
    letter-spacing: -0.025em;
    margin: 0 0 14px;
    line-height: 1.25;
  }

  .ei-desc {
    font-size: 14.5px;
    color: #8b9dc3;
    line-height: 1.7;
    max-width: 480px;
    margin: 0 auto 32px;
  }

  /* Feedback button — cyan pill */
  .ei-btn {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 12px 28px;
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
    color: #04111a;
    border-radius: 50px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(34, 211, 238, 0.28);
    transition: filter 0.15s, transform 0.12s, box-shadow 0.15s;
    white-space: nowrap;
    letter-spacing: 0.01em;
    margin: 0 auto;
  }

  .ei-btn:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
    box-shadow: 0 6px 28px rgba(34, 211, 238, 0.40);
  }

  .ei-btn:active { transform: translateY(0); }

  /* Feedback submitted badge */
  .ei-submitted {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: rgba(74, 222, 128, 0.08);
    border: 1px solid rgba(74, 222, 128, 0.22);
    border-radius: 10px;
    color: #4ade80;
    font-size: 14px;
    font-weight: 500;
  }

  /* "What happens next" box */
  .ei-next {
    margin-top: 32px;
    padding: 20px 24px;
    background: rgba(255,255,255,0.03);
    border: 1px solid #1e2d4a;
    border-radius: 12px;
    text-align: left;
  }

  .ei-next-title {
    font-size: 12.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #6b7fa8;
    margin: 0 0 12px;
  }

  .ei-next ul {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .ei-next li {
    font-size: 13.5px;
    color: #8b9dc3;
    line-height: 1.55;
  }

  @media (max-width: 540px) {
    .ei-card { padding: 36px 20px; }
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const EndInterview = () => {
  inject();
  const { stopMedia, userOwnData } = useContext(CommonContext);
  const [showEditor, setShowEditor] = useState(false);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [titleDescription, setTitleDescription] = useState({
    title: "Interview Successfully Conducted",
    description: "The interview has been completed. Please review and submit your feedback to proceed with the hiring process.",
  });

  useEffect(() => { stopMedia(); }, []); // eslint-disable-line

  return (
    <>
      <FeedbackPopup
        show={showEditor}
        handleClose={() => setShowEditor(false)}
        setTitleDescription={setTitleDescription}
        setIsFeedbackSubmitted={setIsFeedbackSubmitted}
      />

      <JoinHeader />

      <div className="ei-page">
        <div className="ei-card">

          {/* Icon */}
          <div className="ei-icon">
            <FaCheckCircle />
          </div>

          {/* Title */}
          <h2 className="ei-title">
            {userOwnData.isCandidate
              ? "Interview Successfully Completed"
              : titleDescription.title}
          </h2>

          {/* Description */}
          <p className="ei-desc">
            {userOwnData.isCandidate
              ? "Great job! We'll review your performance and get back to you with the next steps soon."
              : titleDescription.description}
          </p>

          {/* Feedback button */}
          {!userOwnData.isCandidate && !isFeedbackSubmitted && (
            <button className="ei-btn" onClick={() => setShowEditor(true)}>
              <FaComments size={16} />
              Give Feedback
            </button>
          )}

          {/* Feedback submitted */}
          {!userOwnData.isCandidate && isFeedbackSubmitted && (
            <div className="ei-submitted">
              <FaCheckCircle size={15} />
              Feedback submitted successfully!
            </div>
          )}

          {/* What happens next — candidates only */}
          {userOwnData.isCandidate && (
            <div className="ei-next">
              <p className="ei-next-title">What happens next?</p>
              <ul>
                <li>Your interview recording will be reviewed by our team</li>
                <li>We'll assess your responses and technical skills</li>
                <li>You'll receive an update soon</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EndInterview;